import { useMemo } from 'react'
import DataGrid from '@components/common/DataGrid'
import useTicker from '@hooks/useTicker'
import JobStatusCell from './JobStatusCell'
import JobRowActions from './JobRowActions'
import { fmtDuration, fmtRows, fmtWhen } from './queueFormat'
import { RefreshIcon } from './queueIcons'
import { jobTrigger } from './jobIdentity'

/**
 * The queue's job table, on the shared DataGrid (never a hand-rolled <table>) —
 * which also gives Columns + Export CSV of the queue for free.
 *
 * Column order and names match Himaad's Section 2 (`Queue / All` 589:9723):
 * Report · Status · Rows · Duration · When · action · kebab. `Time`/`Finished`
 * were ours; the design's words are better and, more usefully, describe what the
 * cells actually hold — the last column has always shown a scheduled run's FUTURE
 * time as well as a finished one's, so "Finished" was wrong for a third of the
 * states it rendered.
 *
 * The report NAME is unambiguously the first thing read on every row (13.5px/600
 * navy); id and owner drop to an 11.5px grey line; status is the deliberate
 * second read.
 *
 * ⚠ `columns` must NOT depend on a ticking clock. It used to take `now` as a
 * prop, so the memo produced a new array every second, TanStack rebuilt the cell
 * renderers, and each row's KebabMenu remounted — which is why the "⋯" menu
 * appeared to close itself the instant it opened. Cells that need a clock own
 * one (JobStatusCell, FinishedCell below).
 */

/**
 * "Today 2:59 PM" — relative to a coarse clock this cell owns, not a prop.
 *
 * A `scheduled` job hasn't finished, so a bare timestamp reads as a past run when
 * it is really WHEN IT WILL RUN — hence the "Runs" prefix on that one state. The
 * column is now headed `When`, which covers both without the prefix having to
 * apologise for the header.
 */
const FinishedCell = ({ job }) => {
  // Minute granularity: the only thing `now` decides here is Today/Yesterday/date.
  const now = useTicker(true, 60_000)
  if (job.state === 'scheduled' && job.scheduledFor) {
    return <span className="text-gray-500">Runs {fmtWhen(job.scheduledFor, now)}</span>
  }
  const at = job.finishedAt ?? job.createdAt
  return <span>{at ? fmtWhen(at, now) : '—'}</span>
}

const QueueTable = ({ jobs, loading, onOpen, onDismiss, onRerun, emptyMessage }) => {
  const columns = useMemo(() => [
    {
      accessorKey: 'displayName',
      header: 'Report',
      cell: ({ row }) => {
        const j = row.original
        const trigger = jobTrigger(j)
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              {/*
                * Q3 — the recurring provenance glyph, Himaad's Section 2 (rows 4
                * and 5 carry it). Says at a glance that this run was PRODUCED by a
                * schedule rather than typed by a person, which changes what the
                * row means: nobody was sitting there, and its result is kept by
                * count (and, like every result, for at most the bucket's age — spec
                * 152) rather than for a week.
                *
                * Possible at all only because `scheduleId` now reaches the client
                * (reportJobService.normalizeJob). Before that this distinction was
                * unavailable on the row, which is why three shipped branches that
                * tested for it were dead code.
                *
                * `title` AND `sr-only` text: a glyph with only a tooltip is
                * invisible to a screen reader, and this one carries information no
                * other cell repeats.
                */}
              {/*
                * Spec 132: the words come from `jobTrigger`, the drawer's own Trigger
                * rule, so a Run-now row no longer says "Ran on a schedule" here while
                * its drawer says otherwise (14 of 14 live Run-now rows did).
                */}
              {trigger.fromSchedule && (
                <span title={trigger.note ? `${trigger.label} · ${trigger.note}` : trigger.label} className="shrink-0 text-rc-chip-text">
                  <RefreshIcon className="h-3.5 w-3.5" />
                  <span className="sr-only">{`${trigger.label}.`}</span>
                </span>
              )}
              <div className="min-w-0 truncate text-[13.5px] font-semibold leading-tight text-cb-navy">{j.displayName}</div>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-gray-500">
              {/*
                * The `kind` chip is GONE. It printed our internal discriminator
                * ("generic" / "specialized" / "accounting") as user-facing
                * vocabulary on every row — nobody outside this codebase knows what
                * a generic report is, and it was the widest thing on the line.
                * `kind` is still read, for the drawer's re-run gate, where it
                * decides behaviour instead of being displayed.
                *
                * CC-50231 TC4b: `· you` used to be a hardcoded literal, so under
                * firm/group scope every one of a colleague's runs was attributed to
                * the reader. `!== false` (not truthiness) so an older worker payload
                * that omits the flag still reads as "mine" — the flag only exists at
                * all in a widened listing. There is deliberately no owner NAME here:
                * the list endpoint strips `user_id` and serves no identity field, and
                * inventing one client-side is not an option.
                */}
              <span>{j.reportId ? `#${j.reportId}` : String(j.id).slice(0, 8)} · {j.isOwner !== false ? 'you' : 'another user'}</span>
            </div>
          </div>
        )
      },
    },
    {
      id: 'status',
      accessorFn: (j) => j.effectiveState || j.state,
      header: 'Status',
      cell: ({ row }) => <JobStatusCell job={row.original} />,
    },
    {
      accessorKey: 'rowCount',
      header: 'Rows',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="tabular-nums font-semibold text-gray-900">{fmtRows(row.original.rowCount)}</span>
      ),
    },
    {
      accessorKey: 'durationMs',
      header: 'Duration',
      meta: { align: 'right' },
      cell: ({ row }) => <span className="tabular-nums">{fmtDuration(row.original.durationMs)}</span>,
    },
    {
      accessorKey: 'finishedAt',
      header: 'When',
      cell: ({ row }) => <FinishedCell job={row.original} />,
    },
    {
      id: 'actions',
      // Deliberately blank ABOVE the table — a heading over the buttons is noise.
      // But the Columns picker needs words (CC-50288), so name it there instead.
      // "Actions", not "View report": the primary button is state-dependent
      // (View report / Watch / Fix & retry / Run again) and the kebab carries five
      // more, so naming the column after one of them would be wrong four ways.
      header: '',
      meta: { pickerLabel: 'Actions' },
      enableSorting: false,
      cell: ({ row }) => (
        <JobRowActions job={row.original} onOpen={onOpen} onDismiss={onDismiss} onRerun={onRerun} />
      ),
    },
  ], [onOpen, onDismiss, onRerun])

  return (
    <DataGrid
      columns={columns}
      data={jobs}
      isLoading={loading}
      emptyMessage={emptyMessage}
      loadingMessage="Loading your queue…"
      onRowClick={(j) => onOpen?.(j)}
      storageKey="report-queue"
      rowNoun="job"
      exportFilename="report-queue"
      stickyHeader
      normalCaseHeaders
      // The queue has ONE filter box above the table. DataGrid's per-column
      // filter row would add six more inputs directly under the headers — the
      // exact "chrome competing with the table" the design review cut the stat
      // tiles to avoid.
      enableFiltering={false}
      // DataGrid hides its pager while everything fits on one page. The list
      // endpoint returns at most 25 jobs, so the default page size of 50 meant
      // the pager could never appear. 10 keeps the queue to a readable screen and
      // makes paging real for anyone with a busy day. (Paging BEYOND the server's
      // 25 needs the cursor work in CC-50150 — this is client-side only.)
      pageSize={10}
      // CC-50262: the queue polls every 5s, and each poll hands down a new array.
      // Without this the reader is bounced back to page 1 seconds after paging,
      // having done nothing. Opt-in per grid — see DataGrid's note on why this is
      // not the global default.
      keepPageOnDataChange
      defaultSorting={[{ id: 'finishedAt', desc: true }]}
    />
  )
}

export default QueueTable
